(() => {
  const showCookiePreferences = async () => {
    const status = document.querySelector('[data-mapia-cookie-status]');
    try {
      if (!window.privacyBanner?.showPreferences) throw new Error('Banner unavailable');
      await window.privacyBanner.showPreferences();
      if (status) status.textContent = '';
    } catch {
      if (status) status.textContent = 'Cookie preferences could not open. Please reload and try again.';
    }
  };
  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('[data-mapia-cookie-preferences]')) {
      event.preventDefault();
      void showCookiePreferences();
    }
  });
  const openLinkedPreferences = () => {
    if (window.location.hash === '#cookie-preferences') void showCookiePreferences();
  };
  window.addEventListener('load', openLinkedPreferences);
  window.addEventListener('hashchange', openLinkedPreferences);
  const campaignKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  const anonymousIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  let analyticsAllowed = false;

  const cookieValue = (name) => {
    const prefix = `${name}=`;
    return document.cookie.split(";").map((value) => value.trim()).find((value) => value.startsWith(prefix))?.slice(prefix.length) || "";
  };

  const cookieDomain = () => {
    const host = window.location.hostname;
    return host === "mapia.studio" || host.endsWith(".mapia.studio") ? "; Domain=.mapia.studio" : "";
  };

  const setConsentCookies = (allowed) => {
    analyticsAllowed = allowed === true;
    const common = `; Path=/; SameSite=Lax; Secure${cookieDomain()}`;
    if (!analyticsAllowed) {
      document.cookie = `mapia_analytics_consent=; Max-Age=0${common}`;
      document.cookie = `mapia_aid=; Max-Age=0${common}`;
      window.MapiaAttribution?.clear();
      return;
    }
    document.cookie = `mapia_analytics_consent=1; Max-Age=15552000${common}`;
    const existing = cookieValue("mapia_aid");
    if (!anonymousIdPattern.test(existing)) {
      document.cookie = `mapia_aid=${crypto.randomUUID()}; Max-Age=2592000${common}`;
    }
  };

  const customerPrivacyAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
    } catch {
      return false;
    }
  };

  const initializeConsent = () => new Promise((resolve) => {
    if (!window.Shopify?.loadFeatures) {
      setConsentCookies(false);
      resolve(false);
      return;
    }
    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
      const allowed = !error && customerPrivacyAllowed();
      setConsentCookies(allowed);
      resolve(allowed);
    });
  });

  const publish = (eventName, context = {}) => {
    if (!analyticsAllowed || !window.Shopify?.analytics?.publish) return;
    window.Shopify.analytics.publish(eventName, context);
  };

  const creatorUrlWithAttribution = (rawUrl) => {
    const target = new URL(rawUrl, window.location.origin);
    campaignKeys.forEach(key => target.searchParams.delete(key));
    const attribution = analyticsAllowed ? window.MapiaAttribution?.capture() || {} : {};
    Object.entries(attribution).forEach(([key, value]) => target.searchParams.set(key, value));
    return target.toString();
  };

  const bindCreatorAttribution = (root = document) => {
    root.querySelectorAll('a[href^="https://app.mapia.studio/create"]').forEach((link) => {
      link.href = creatorUrlWithAttribution(link.href);
    });
  };

  const reconcileMapiaCartHandoff = async () => {
    const pageUrl = new URL(window.location.href);
    const bundleHandoff = pageUrl.searchParams.get("mapia_bundle_handoff");
    if (bundleHandoff && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(bundleHandoff)) {
      const status = document.querySelector("[data-mapia-cart-status]");
      const checkout = document.querySelector('button[name="checkout"]');
      if (checkout) checkout.disabled = true;
      if (status) { status.hidden = false; status.textContent = "Checking your chosen artworks…"; }
      const root = window.Shopify?.routes?.root || "/";
      try {
        const response = await fetch(`${root}cart.js`, { headers: { accept: "application/json" } });
        if (!response.ok) throw new Error("Cart unavailable");
        const cart = await response.json();
        const items = Array.isArray(cart.items) ? cart.items : [];
        const current = items.filter(item => item?.properties?._mapia_bundle_handoff_id === bundleHandoff);
        const bundleId = current[0]?.properties?._mapia_bundle_id;
        if (!bundleId || current.length < 1 || current.length > 3 || current.some(item => item.properties._mapia_bundle_id !== bundleId)
          || current.some(item => item.properties._mapia_bundle_selected_count !== String(current.length))
          || new Set(current.map(item => item.properties._mapia_style_id)).size !== current.length) throw new Error("Selection unavailable");
        // Retire only older selections from this same private request. Unrelated
        // cart contents stay visible for the customer to resolve explicitly.
        const updates = Object.fromEntries(items.filter(item => item?.properties?._mapia_bundle_id === bundleId
          && item?.properties?._mapia_bundle_handoff_id && item.properties._mapia_bundle_handoff_id !== bundleHandoff).map(item => [item.key, 0]));
        current.filter(item => item.quantity !== 1).forEach(item => { updates[item.key] = 1; });
        pageUrl.searchParams.delete("mapia_bundle_handoff");
        if (Object.keys(updates).length) {
          const update = await fetch(`${root}cart/update.js`, { method: "POST", headers: { accept: "application/json", "content-type": "application/json" }, body: JSON.stringify({ updates }) });
          if (!update.ok) throw new Error("Selection update unavailable");
          window.location.replace(pageUrl.toString());
          return;
        }
        window.history.replaceState({}, "", pageUrl);
        if (checkout) checkout.disabled = false;
        if (status) status.hidden = true;
      } catch {
        if (status) { status.hidden = false; status.textContent = "We couldn’t check your selection. Refresh this page, or return to your gallery and choose again before paying."; }
      }
      return;
    }
    const handoffId = pageUrl.searchParams.get("mapia_handoff");
    if (!handoffId || handoffId === "complete" || !/^[A-Za-z0-9_-]{8,20}$/.test(handoffId)) return;

    const status = document.querySelector("[data-mapia-cart-status]");
    if (status instanceof HTMLElement) {
      status.hidden = false;
      status.textContent = "Refreshing your secure artwork cart…";
    }
    const root = window.Shopify?.routes?.root || "/";
    try {
      const cartResponse = await fetch(`${root}cart.js`, { headers: { accept: "application/json" } });
      if (!cartResponse.ok) throw new Error("Cart could not be loaded.");
      const cart = await cartResponse.json();
      const items = Array.isArray(cart.items) ? cart.items : [];
      const current = items.find((item) => item?.properties?._mapia_handoff_id === handoffId);
      if (!current?.properties?._mapia_intake_id) throw new Error("The new artwork was not found in the cart.");

      const stale = items.filter((item) =>
        item?.properties?._mapia_intake_id === current.properties._mapia_intake_id
        && item?.properties?._mapia_handoff_id !== handoffId
      );
      if (stale.length) {
        const updates = Object.fromEntries(stale.map((item) => [item.key, 0]));
        const updateResponse = await fetch(`${root}cart/update.js`, {
          method: "POST",
          headers: { accept: "application/json", "content-type": "application/json" },
          body: JSON.stringify({ updates }),
        });
        if (!updateResponse.ok) throw new Error("The previous artwork reference could not be retired.");
        window.location.replace(`${root}cart?mapia_handoff=complete`);
        return;
      }
      pageUrl.searchParams.set("mapia_handoff", "complete");
      window.history.replaceState({}, "", pageUrl);
      if (status instanceof HTMLElement) status.hidden = true;
    } catch {
      if (status instanceof HTMLElement) {
        status.hidden = false;
        status.textContent = "We could not refresh this cart automatically. Remove the older copy of this artwork before continuing.";
      }
    }
  };

  const bindArtworkFallbacks = (root = document) => {
    root.querySelectorAll("[data-artwork-media] img").forEach((image) => {
      if (image.dataset.fallbackBound === "true") return;
      image.dataset.fallbackBound = "true";
      const markMissing = () => image.closest("[data-artwork-media]")?.classList.add("is-missing");
      image.addEventListener("error", markMissing, { once: true });
      if (image.complete && image.naturalWidth === 0) markMissing();
    });
  };

  const closeMenu = (details, restoreFocus = false) => {
    if (!details.open) return;
    details.open = false;
    details.querySelector("summary")?.setAttribute("aria-expanded", "false");
    if (restoreFocus) details.querySelector("summary")?.focus();
  };

  // Keep only the compact navigation sticky, including when announcement copy wraps.
  const announcement = document.querySelector('.site-announcement');
  const headerGroup = announcement?.closest('.shopify-section-group-header-group');
  if (headerGroup && window.ResizeObserver) {
    const updateHeaderOffset = () => headerGroup.style.setProperty('--mapia-announcement-height', `${announcement.getBoundingClientRect().height}px`);
    updateHeaderOffset();
    new ResizeObserver(updateHeaderOffset).observe(announcement);
  }

  document.querySelectorAll("[data-mobile-menu]").forEach((details) => {
    const summary = details.querySelector("summary");
    if (!summary) return;
    details.addEventListener("toggle", () => {
      summary.setAttribute("aria-expanded", String(details.open));
      summary.setAttribute("aria-label", details.open ? "Close navigation" : "Open navigation");
    });
    details.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeMenu(details, true);
    });
    details.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => closeMenu(details)));
    document.addEventListener("pointerdown", (event) => {
      if (details.open && !details.contains(event.target)) closeMenu(details);
    });
  });

  class MapiaStyleExplorer extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute("ready")) return;
      this.options = [...this.querySelectorAll("[data-style-option]")];
      this.panels = [...this.querySelectorAll("[data-style-panel]")];
      this.filters = [...this.querySelectorAll("[data-style-filter]")];
      if (!this.options.length || !this.panels.length) return;

      this.options.forEach((option, index) => {
        option.addEventListener("click", () => this.select(option.dataset.styleOption));
        option.addEventListener("keydown", (event) => this.handleKeydown(event, index));
      });
      this.filters.forEach((filter) => {
        filter.addEventListener("click", () => this.filter(filter.dataset.styleFilter));
      });
      if (this.hasAttribute('data-mobile-carousel')) this.bindMobileCarousel();
      this.select(this.options[0].dataset.styleOption, false);
      this.setAttribute("ready", "");
      // WebKit can snap to the last slide when the enhanced layout first
      // replaces the stacked fallback. Align after that layout has taken effect.
      if (this.mobileQuery?.matches) this.scrollToActive('instant');
    }

    bindMobileCarousel() {
      this.stage = this.querySelector('.style-explorer__stage');
      this.mobileQuery = matchMedia('(max-width: 47.99rem)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.previousButton = this.querySelector('[data-style-previous]');
      this.nextButton = this.querySelector('[data-style-next]');
      this.positionLabel = this.querySelector('[data-style-position]');
      this.announcementLabel = this.querySelector('[data-style-announcement]');
      this.previousButton.addEventListener('click', () => this.select(this.options[Math.max(0, this.activeIndex - 1)].dataset.styleOption));
      this.nextButton.addEventListener('click', () => this.select(this.options[Math.min(this.options.length - 1, this.activeIndex + 1)].dataset.styleOption));
      this.stage.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          if (!this.mobileQuery.matches) return;
          const left = this.stage.getBoundingClientRect().left;
          const nearest = this.panels.reduce((best, panel, index) =>
            Math.abs(panel.getBoundingClientRect().left - left) < Math.abs(this.panels[best].getBoundingClientRect().left - left) ? index : best, 0);
          this.select(this.options[nearest].dataset.styleOption, false, false);
        }, 100);
      }, { passive: true });
      this.stage.addEventListener('keydown', (event) => {
        if (!this.mobileQuery.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.options.length - 1
          : Math.max(0, Math.min(this.options.length - 1, this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1)));
        this.select(this.options[next].dataset.styleOption);
      });
      this.onLayoutChange = () => {
        this.select(this.options[this.activeIndex || 0].dataset.styleOption, false, false);
        if (this.mobileQuery.matches) this.scrollToActive('instant');
      };
      this.mobileQuery.addEventListener('change', this.onLayoutChange);
    }

    disconnectedCallback() {
      clearTimeout(this.scrollTimer);
      this.mobileQuery?.removeEventListener('change', this.onLayoutChange);
    }

    scrollToActive(behavior) {
      const target = this.panels[this.activeIndex];
      const left = target.getBoundingClientRect().left - this.stage.getBoundingClientRect().left + this.stage.scrollLeft;
      this.stage.scrollTo({ left, behavior });
    }

    select(id, moveFocus = false, scroll = true) {
      const selected = this.options.find((option) => option.dataset.styleOption === id && !option.hidden);
      if (!selected) return;
      const previousIndex = this.activeIndex;
      this.activeIndex = this.options.indexOf(selected);
      const mobile = this.mobileQuery?.matches === true;
      this.options.forEach((option) => {
        const active = option === selected;
        option.setAttribute("aria-selected", String(active));
        option.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel, index) => {
        const active = panel.dataset.stylePanel === id;
        panel.hidden = !mobile && !active;
        if (mobile) {
          panel.removeAttribute('aria-hidden');
          panel.removeAttribute('aria-labelledby');
          panel.setAttribute('role', 'group');
          panel.setAttribute('aria-roledescription', 'slide');
          panel.setAttribute('aria-label', `${panel.dataset.styleName}, ${index + 1} of ${this.panels.length}`);
        } else {
          panel.setAttribute('aria-hidden', String(!active));
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', this.options[index].id);
          panel.removeAttribute('aria-roledescription');
          panel.removeAttribute('aria-label');
        }
      });
      if (this.stage) {
        this.stage.tabIndex = mobile ? 0 : -1;
        if (mobile) {
          this.stage.setAttribute('role', 'region');
          this.stage.setAttribute('aria-label', 'Artwork styles. Swipe or use the previous and next buttons.');
        } else {
          this.stage.removeAttribute('role');
          this.stage.removeAttribute('aria-label');
        }
        this.previousButton.disabled = this.activeIndex === 0;
        this.nextButton.disabled = this.activeIndex === this.options.length - 1;
        this.positionLabel.textContent = `${this.activeIndex + 1} / ${this.options.length}`;
        if (previousIndex !== undefined && previousIndex !== this.activeIndex) this.announcementLabel.textContent = `${selected.textContent.trim()}, ${this.activeIndex + 1} of ${this.options.length}`;
        if (mobile && scroll && previousIndex !== undefined) this.scrollToActive(this.reducedMotion.matches ? 'instant' : 'smooth');
      }
      if (previousIndex !== this.activeIndex) publish("mapia:style_viewed", {
        style_id: selected.dataset.styleOption || "",
        orientation: selected.dataset.orientation || "",
      });
      if (moveFocus && !mobile) selected.focus();
    }

    filter(orientation) {
      this.filters.forEach((filter) => {
        filter.setAttribute("aria-pressed", String(filter.dataset.styleFilter === orientation));
      });
      this.options.forEach((option) => {
        option.hidden = orientation !== "all" && option.dataset.orientation !== orientation;
      });
      const active = this.options.find((option) => option.getAttribute("aria-selected") === "true" && !option.hidden);
      if (!active) this.select(this.options.find((option) => !option.hidden)?.dataset.styleOption);
    }

    handleKeydown(event, index) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const visible = this.options.filter((option) => !option.hidden);
      const current = visible.indexOf(this.options[index]);
      let next = current;
      if (event.key === 'ArrowRight') next = (current + 1) % visible.length;
      if (event.key === 'ArrowLeft') next = (current - 1 + visible.length) % visible.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = visible.length - 1;
      this.select(visible[next].dataset.styleOption, true);
    }
  }

  if (!customElements.get("mapia-style-explorer")) {
    customElements.define("mapia-style-explorer", MapiaStyleExplorer);
  }

  class MapiaStyleGallery extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute("ready")) return;
      this.options = [...this.querySelectorAll("[data-style-gallery-option]")];
      this.panels = [...this.querySelectorAll("[data-style-gallery-panel]")];
      if (!this.options.length || !this.panels.length) return;

      this.options.forEach((option, index) => {
        option.addEventListener("click", () => this.select(option.dataset.styleGalleryOption));
        option.addEventListener("keydown", (event) => this.handleKeydown(event, index));
      });
      this.select(this.options[0].dataset.styleGalleryOption, false);
      this.setAttribute("ready", "");
    }

    select(id, moveFocus = false) {
      const selected = this.options.find((option) => option.dataset.styleGalleryOption === id);
      if (!selected) return;
      this.options.forEach((option) => {
        const active = option === selected;
        option.setAttribute("aria-selected", String(active));
        option.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.styleGalleryPanel === id;
        panel.hidden = !active;
        panel.setAttribute("aria-hidden", String(!active));
      });
      publish("mapia:style_viewed", {
        style_id: selected.dataset.styleGalleryOption || "",
        capability_id: selected.dataset.capabilityId || "",
        orientation: selected.dataset.orientation || "portrait",
      });
      if (moveFocus) selected.focus();
    }

    handleKeydown(event, index) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % this.options.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + this.options.length) % this.options.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.options.length - 1;
      this.select(this.options[next].dataset.styleGalleryOption, true);
    }
  }

  if (!customElements.get("mapia-style-gallery")) {
    customElements.define("mapia-style-gallery", MapiaStyleGallery);
  }

  class MapiaProductConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute("ready")) return;
      const data = this.querySelector("[data-product-variants]")?.textContent;
      try {
        this.variants = JSON.parse(data || "[]");
      } catch {
        this.variants = [];
      }
      this.inputs = [...this.querySelectorAll("[data-product-option] input")];
      this.priceTargets = [...this.querySelectorAll("[data-product-price]")];
      this.ctas = [...this.querySelectorAll("[data-product-cta]")];
      this.availability = this.querySelector("[data-product-availability]");
      this.pendingPrice = this.dataset.pricePending || "Price to be confirmed";
      this.stickyAction = this.querySelector(".mapia-product-sticky");
      this.primaryAction = this.querySelector(".mapia-product__cta");

      this.inputs.forEach((input) => input.addEventListener("change", () => this.choose(input)));
      this.disableUnavailableValues();
      const selectedId = new URL(window.location.href).searchParams.get("variant");
      const initial = this.variants.find((variant) => String(variant.id) === selectedId && variant.available)
        || this.variants.find((variant) => variant.available)
        || this.variants[0];
      if (initial) this.select(initial, false);
      this.bindStickyAction();
      this.setAttribute("ready", "");
    }

    bindStickyAction() {
      if (!this.stickyAction || !this.primaryAction || !("IntersectionObserver" in window)) return;
      this.actionObserver = new IntersectionObserver(([entry]) => {
        const hasPassedAction = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.stickyAction.classList.toggle("is-visible", hasPassedAction);
      });
      this.actionObserver.observe(this.primaryAction);
    }

    currentOptions() {
      return [...this.querySelectorAll("[data-product-option]")].map((group) => group.querySelector("input:checked")?.value);
    }

    choose(input) {
      const index = Number(input.closest("[data-product-option]")?.dataset.optionIndex || 0);
      const current = this.currentOptions();
      const candidates = this.variants
        .filter((variant) => variant.available && variant.options[index] === input.value)
        .sort((left, right) => {
          const score = (variant) => variant.options.reduce((total, value, optionIndex) => total + (value === current[optionIndex] ? 1 : 0), 0);
          return score(right) - score(left);
        });
      if (candidates[0]) this.select(candidates[0]);
    }

    disableUnavailableValues() {
      const current = this.currentOptions();
      this.inputs.forEach((input) => {
        const index = Number(input.closest("[data-product-option]")?.dataset.optionIndex || 0);
        input.disabled = !this.variants.some((variant) => {
          if (!variant.available || variant.options[index] !== input.value) return false;
          return variant.options.slice(0, index).every((value, previousIndex) => value === current[previousIndex]);
        });
      });
    }

    select(variant, updateUrl = true) {
      this.inputs.forEach((input) => {
        const index = Number(input.closest("[data-product-option]")?.dataset.optionIndex || 0);
        input.checked = variant.options[index] === input.value;
      });
      this.disableUnavailableValues();

      const price = variant.price > 0 ? variant.priceFormatted : this.pendingPrice;
      this.priceTargets.forEach((target) => { target.textContent = price; });
      if (this.availability) {
        this.availability.textContent = variant.price <= 0
          ? `Launch configuration: ${this.pendingPrice} · 40 × 60 cm framed portrait`
          : variant.available
            ? "Available to begin creating"
            : "This configuration is not currently available";
      }

      const base = this.dataset.creatorUrl;
      const product = encodeURIComponent(this.dataset.productHandle || "");
      this.ctas.forEach((cta) => {
        if (!variant.available || !base) {
          cta.removeAttribute("href");
          cta.setAttribute("aria-disabled", "true");
          return;
        }
        cta.href = creatorUrlWithAttribution(`${base}?product=${product}&variant=${encodeURIComponent(variant.id)}`);
        cta.removeAttribute("aria-disabled");
      });

      if (updateUrl && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url);
      }
    }
  }

  if (!customElements.get("mapia-product-configurator")) {
    customElements.define("mapia-product-configurator", MapiaProductConfigurator);
  }

  bindArtworkFallbacks();
  void initializeConsent().then(() => bindCreatorAttribution());
  reconcileMapiaCartHandoff();
  document.addEventListener("visitorConsentCollected", () => {
    setConsentCookies(customerPrivacyAllowed());
    bindCreatorAttribution();
  });
  document.addEventListener("shopify:section:load", (event) => {
    bindArtworkFallbacks(event.target);
    bindCreatorAttribution(event.target);
  });
})();
